import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { fileUrl } from "@/lib/placeholders";
import { Button } from "@/components/ui/button";
import QRScanner from "@/components/QRScanner";
import { toast } from "sonner";
import {
  BriefcaseMedical, Building2, ChevronDown, ExternalLink, HardHat,
  ScanLine, Users,
} from "lucide-react";

/**
 * Quello che chi è in visita può sfogliare senza inquadrare un QR.
 *
 * Documenti aziendali, documenti del cantiere, cassette di primo soccorso: le
 * tre cose che il coordinatore chiede sempre. Cercarle una per una gli farebbe
 * perdere tempo mentre i ragazzi aspettano, e a nessuno conviene.
 *
 * Si apre e si legge, punto. Qui dentro non c'è nessun giudizio sul nostro
 * stato di salute: quello che guarda lo vediamo noi nel rapporto della visita,
 * non lui su uno schermo.
 */
const itDate = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : null);

function Sezione({ icona: Icona, titolo, quanti, aperta, onApri, children }) {
  return (
    <Card className="border-slate-200">
      <button type="button" onClick={onApri}
              data-testid={`sezione-${titolo.toLowerCase().replace(/\s/g, "-")}`}
              className="w-full p-4 flex items-center gap-3 text-left">
        <span className="w-10 h-10 rounded-lg bg-[#0067b2] text-white flex items-center justify-center flex-shrink-0">
          <Icona className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-display font-bold">{titolo}</span>
          <span className="block text-xs text-slate-500">{quanti}</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${aperta ? "rotate-180" : ""}`} />
      </button>
      {aperta && <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>}
    </Card>
  );
}

function Documento({ d }) {
  return (
    <div className="flex items-center gap-2 py-2 border-t border-slate-100">
      <span className="flex-1 min-w-0">
        <span className="block text-sm truncate">{d.titolo || d.tipo}</span>
        <span className="block text-[11px] text-slate-400">
          {d.tipo}{d.scadenza && ` · scade il ${itDate(d.scadenza)}`}
        </span>
      </span>
      {d.file_url ? (
        <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer"
           className="text-orange-600 text-sm font-medium flex items-center gap-1 flex-shrink-0">
          apri <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ) : (
        <span className="text-xs text-slate-400 flex-shrink-0">nessun file</span>
      )}
    </div>
  );
}

export default function DocumentiVisita() {
  const { user } = useAuth();
  const idVisita = user?.visita_id;
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [aperta, setAperta] = useState("cantiere");
  const [scanner, setScanner] = useState(false);

  useEffect(() => {
    if (!idVisita) return;
    api.get(`/visite/${idVisita}/consultazione`)
      .then(({ data }) => setDati(data))
      .catch(() => setErrore(true));
  }, [idVisita]);

  if (!idVisita) {
    return <div className="max-w-2xl mx-auto p-6 pt-24 text-center text-slate-500">
      Questa pagina si apre durante una visita in cantiere.
    </div>;
  }
  if (errore) {
    return <div className="max-w-2xl mx-auto p-6 pt-24 text-center text-slate-500">
      La visita non è più aperta. Chiedi un nuovo codice al preposto.
    </div>;
  }
  if (!dati) {
    return <div className="max-w-2xl mx-auto p-6 pt-24 text-slate-500">Caricamento...</div>;
  }

  const apri = (q) => setAperta((a) => (a === q ? null : q));

  return (
    <div className="max-w-2xl mx-auto p-4 pt-20 pb-16 space-y-3" data-testid="documenti-visita">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-1">
          Visita in cantiere
        </div>
        <h1 className="font-display text-2xl font-black tracking-tight">Documenti</h1>
        {dati.cantiere && <p className="text-sm text-slate-500 mt-1">{dati.cantiere}</p>}
      </div>

      {/* Il modo più veloce di arrivare a una scheda è inquadrarla: il
          pulsante sta in cima e occupa tutta la larghezza, perché è quello
          che si tocca dieci volte in un sopralluogo. */}
      <Button onClick={() => setScanner(true)} data-testid="scan-visita"
              className="w-full h-20 text-lg bg-orange-500 hover:bg-orange-600 text-white">
        <ScanLine className="w-7 h-7 mr-3" /> Inquadra un QR
      </Button>
      <p className="text-xs text-slate-500 -mt-1 mb-1">
        Persone, mezzi e attrezzature: sulla scheda trovi attestati, documenti
        e manutenzioni.
      </p>

      <Sezione icona={HardHat} titolo="Documenti del cantiere"
               quanti={`${dati.cantiere_documenti.length} documenti · POS, PSC, ordinanze, notifica`}
               aperta={aperta === "cantiere"} onApri={() => apri("cantiere")}>
        {dati.cantiere_documenti.length === 0
          ? <p className="text-sm text-slate-500 pt-2">Nessun documento caricato.</p>
          : dati.cantiere_documenti.map((d, i) => <Documento key={i} d={d} />)}
      </Sezione>

      <Sezione icona={Building2} titolo="Documenti aziendali"
               quanti={`${dati.aziendali.length} documenti · DURC, CCIAA, iscrizioni`}
               aperta={aperta === "azienda"} onApri={() => apri("azienda")}>
        {dati.aziendali.length === 0
          ? <p className="text-sm text-slate-500 pt-2">Nessun documento caricato.</p>
          : dati.aziendali.map((d, i) => <Documento key={i} d={d} />)}
      </Sezione>

      <Sezione icona={Users} titolo="Squadra in cantiere"
               quanti={`${(dati.squadra || []).length} persone`}
               aperta={aperta === "squadra"} onApri={() => apri("squadra")}>
        {(dati.squadra || []).length === 0
          ? <p className="text-sm text-slate-500 pt-2">Nessuna persona assegnata a questo cantiere.</p>
          : dati.squadra.map((p) => (
              <div key={p.id} className="flex items-center gap-2 py-2 border-t border-slate-100">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{p.full_name}</span>
                  <span className="block text-[11px] text-slate-400">
                    {p.role}{p.badge_number ? ` · matricola ${p.badge_number}` : ""}
                  </span>
                </span>
                <a href={`/fascicolo/${p.id}?tesserino=1`} className="text-orange-600 text-sm font-medium flex-shrink-0">
                  tesserino
                </a>
              </div>
            ))}
      </Sezione>

      <Sezione icona={BriefcaseMedical} titolo="Cassette di primo soccorso"
               quanti={`${dati.cassette.length} sui mezzi di questo cantiere`}
               aperta={aperta === "cassette"} onApri={() => apri("cassette")}>
        {dati.cassette.length === 0
          ? <p className="text-sm text-slate-500 pt-2">
              Nessun mezzo di questo cantiere ha la cassetta a scheda.
            </p>
          : dati.cassette.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-2 border-t border-slate-100">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{c.mezzo}</span>
                  <span className="block text-[11px] text-slate-400">
                    {c.targa}
                    {c.ultimo_controllo && ` · controllata il ${itDate(c.ultimo_controllo)}`}
                    {c.prima_scadenza && ` · primo prodotto in scadenza il ${itDate(c.prima_scadenza)}`}
                  </span>
                </span>
                <a href={`/attrezzatura/${c.equipment_id || ""}`}
                   className="text-orange-600 text-sm font-medium flex-shrink-0">
                  vedi
                </a>
              </div>
            ))}
      </Sezione>

      {scanner && (
        <QRScanner pubblico onClose={() => setScanner(false)}
                   onScanned={(target) => {
                     setScanner(false);
                     toast.success(`${target.label} riconosciuto`);
                     window.location.href = target.path;
                   }} />
      )}
    </div>
  );
}
